import { next, ids } from "app/utils";
import React from "react";
import { Field, untouch, change, touch } from "redux-form";
import {
  Colors,
  InputGroup,
  Button,
  Tooltip,
  Position,
  Intent,
  Checkbox,
  Classes
} from "@blueprintjs/core";
import styled from "styled-components";

export const Form = styled.form`
  & div,
  & textarea {
    width: 100%;
  }
`;

export const FormRow = styled.div`
  margin-bottom: 10px;
`;

const TickIcon = styled.span.attrs({
  className: "pt-icon-large pt-icon-small-tick"
})`
  .pt-icon-large& {
    color: ${Colors.GREEN3};
    margin-left: 3px;
  }
`;

const ErrorTooltip = props => {
  const { content, isOpen, children } = props;
  return (
    <Tooltip
      content={content}
      isOpen={isOpen}
      position={Position.BOTTOM_LEFT}
      intent={Intent.DANGER}
      inline={false}
      useSmartArrowPositioning={true}
      rootElementTag="div"
    >
      {children}
    </Tooltip>
  );
};

export const CustomInputGroup = ({
  input,
  id,
  type,
  untouchField,
  showError,
  showSuccess,
  meta,
  label,
  rightElement,
  className
}) => {
  return (
    <InputGroup
      {...input}
      id={id}
      className={className}
      type={type}
      onKeyUp={e => e.keyCode === 27 && untouchField()}
      onChange={e => {
        untouchField();
        input.onChange(e);
      }}
      intent={
        showError ? Intent.DANGER : showSuccess && meta.active && Intent.SUCCESS
      }
      placeholder={label}
      rightElement={rightElement}
    />
  );
};

export const CustomTextArea = ({
  input,
  id,
  type,
  untouchField,
  showError,
  showSuccess,
  meta,
  label,
  className,
  rows,
  maxLength
}) => {
  const ptIntent = showError
    ? "pt-intent-danger"
    : showSuccess && meta.active && "pt-intent-success";
  let optionalProps = {};
  if (typeof maxLength === "number" && !isNaN(maxLength)) {
    optionalProps.maxLength = maxLength;
  }
  return (
    <textarea
      {...input}
      {...optionalProps}
      rows={rows}
      id={id}
      dir="auto"
      className={`pt-input ${ptIntent} ${className}`}
      type={type}
      onKeyUp={e => e.keyCode === 27 && untouchField()}
      onChange={e => {
        untouchField();
        input.onChange(e);
      }}
      placeholder={label}
    />
  );
};

export const labelled = Component => props => {
  const { id, meta, label, showError, showSuccess } = props;
  const sureId = id || next(ids);
  return (
    <FormRow>
      <label className="pt-label" htmlFor={sureId}>
        {label}
        {showSuccess && <TickIcon />}
      </label>
      <ErrorTooltip content={meta.error} isOpen={showError}>
        <Component {...props} id={sureId} />
      </ErrorTooltip>
    </FormRow>
  );
};

export const unlabelled = Component => props => {
  const { meta, showError } = props;
  return (
    <FormRow>
      <ErrorTooltip content={meta.error} isOpen={showError}>
        <Component {...props} />
      </ErrorTooltip>
    </FormRow>
  );
};

export const interactive = Component => props => {
  const { input, meta } = props;
  const action = untouch(meta.form, input.name);
  const untouchField = () => {
    meta.dispatch(action);
  };
  const showError = meta.touched && meta.error !== undefined;
  const showSuccess = !meta.pristine && !meta.error;
  return (
    <Component
      {...props}
      untouchField={untouchField}
      showError={showError}
      showSuccess={showSuccess}
    />
  );
};

export const reduxField = Component => props => (
  <Field {...props} component={Component} />
);

const ShowPasswordButton = props => {
  const { tooltip, iconName, onClick } = props;
  return (
    <Tooltip content={tooltip} isDisabled={false}>
      <Button
        className={Classes.MINIMAL}
        intent={Intent.WARNING}
        iconName={iconName}
        onClick={onClick}
      />
    </Tooltip>
  );
};

export class PasswordField extends React.Component {
  state = { isVisible: false };

  handleButtonClick = () => {
    this.setState({ isVisible: !this.state.isVisible });
  };

  componentWillReceiveProps({ submitting }) {
    if (submitting && this.state.isVisible) {
      this.setState({ isVisible: false });
    }
  }

  render() {
    const { isVisible } = this.state;
    const tooltip = isVisible ? "Hide password" : "Show password";
    const iconName = isVisible ? "eye-open" : "eye-off";
    return (
      <CustomInputGroup
        {...this.props}
        type={isVisible ? "text" : "password"}
        rightElement={
          <ShowPasswordButton
            tooltip={tooltip}
            iconName={iconName}
            onClick={this.handleButtonClick}
          />
        }
      />
    );
  }
}

export const FormError = props => {
  const { error, submitting } = props;
  if (!error || submitting) return null;
  return (
    <div className="pt-callout pt-intent-danger pt-icon-error">
      <div>{error.data.message || "Request failed"}</div>
      <div className="pt-text-muted" style={{ fontSize: "0.857em" }}>
        {error.response.status} {error.response.statusText}
      </div>
    </div>
  );
};

export const SubmitButton = props => {
  const { valid, submitting, text, className } = props;
  return (
    <Button
      type="submit"
      disabled={!valid || submitting}
      loading={submitting}
      className={className}
    >
      {text}
    </Button>
  );
};

export const CancelButton = props => {
  const { submitting, text, className, onClick } = props;
  return (
    <Button disabled={submitting} className={className} onClick={onClick}>
      {text}
    </Button>
  );
};

export class CheckboxField extends React.Component {
  constructor(props) {
    super(props);

    const {
      meta: { form, dispatch },
      input: { name }
    } = this.props;

    //   const {
    //     input,
    //     meta: { dispatch },
    //     ...rest
    //   } = props;
    //   this.state = {
    //     input,
    //     dispatch,
    //     rest
    //   };
    //   console.log(rest);
    this.handleChange = this.handleChange.bind(this);
    dispatch(touch(form, name));
  }

  handleChange(event) {
    const {
      meta: { dispatch, form },
      input: { name }
    } = this.props;
    dispatch(change(form, name, event.target.checked));
  }

  render() {
    const { input, showError, showSuccess, untouchField, ...rest } = this.props;

    return (
      <Field
        component={Checkbox}
        props={{
          ...rest,
          onChange: this.handleChange,
          className: `${Classes.CHECKBOX} ${Classes.LARGE}`
        }}
        {...input}
        validate={value => {
          return value ? undefined : "Required";
        }}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/forms.js